<template>
  <v-row>
    <v-col cols="12">
      <v-card class="pr-card" flat>
        <!-- HEADER BAND -->
        <div class="pr-header pl-base-bg">
          <div class="pr-header-icon">
            <v-icon color="#fff" size="22">mdi-account-cash-outline</v-icon>
          </div>
          <div>
            <div class="pr-title">Payroll</div>
            <div class="pr-subtitle">Employee register and monthly gross-to-net payroll runs</div>
          </div>
        </div>

        <div class="pa-6">
          <v-alert type="info" outlined dense border="left" class="pr-disclaimer">
            Gross-to-net posting only — PF/TDS/PT amounts are figures you
            enter yourself (from your CA or payroll provider), not
            computed by statutory slab. This is not a compliance tool;
            challans, Form 16/24Q and PF/ESI filings are outside its scope.
          </v-alert>

          <v-tabs v-model="activeTab" class="pr-tabs">
            <v-tab>Employees</v-tab>
            <v-tab>Payroll Runs</v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab" class="pr-tab-content">
            <v-tab-item>
              <div class="pr-filter-bar">
                <v-row dense align="center">
                  <v-col cols="12" class="pr-actions">
                    <v-btn class="pr-add-btn" @click="openCreateDialog">
                      <v-icon left size="16">mdi-plus</v-icon>
                      Add Employee
                    </v-btn>
                  </v-col>
                </v-row>
              </div>

              <app-ag-grid
                title="Employees"
                :columnDefs="employeeColumnDefs"
                :rowData="employees"
                :newBtn="false"
                @refresh="fetchEmployees"
                :mobileCard="employeeMobileCard"
                height="height: 480px"
              />
            </v-tab-item>

            <v-tab-item>
              <div class="pr-filter-bar">
                <v-row dense align="center">
                  <v-col cols="12" md="2">
                    <v-menu v-model="runPeriodMenu" :close-on-content-click="false" offset-y max-width="290" min-width="290">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="formattedRunPeriod"
                          label="Period"
                          readonly
                          outlined
                          dense
                          hide-details
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker v-model="runPeriod" type="month" @input="runPeriodMenu = false" />
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="10" class="pr-actions">
                    <v-btn class="pr-add-btn" :loading="running" @click="doRunPayroll">
                      <v-icon left size="16">mdi-play-circle-outline</v-icon>
                      Run Payroll
                    </v-btn>
                  </v-col>
                </v-row>
              </div>

              <app-ag-grid
                title="Payroll Runs"
                :columnDefs="payrollRunColumnDefs"
                :rowData="payrollRuns"
                :newBtn="false"
                @refresh="fetchPayrollRuns"
                :mobileCard="payrollRunMobileCard"
                height="height: 480px"
              />
            </v-tab-item>
          </v-tabs-items>
        </div>
      </v-card>
    </v-col>

    <!-- CREATE/EDIT EMPLOYEE DIALOG -->
    <global-dialog
      v-if="employeeDialog"
      v-model="employeeDialog"
      :title="editingEmployee ? 'Edit Employee' : 'Add Employee'"
      icon="mdi-account-cash-outline"
      width="640"
      :buttons="employeeDialogButtons"
    >
      <v-form ref="form" v-model="valid">
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field v-model="form.name" label="Name" outlined dense :rules="[rules.required]" />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field v-model="form.designation" label="Designation" outlined dense />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.grossMonthlySalary"
              label="Gross Monthly Salary (₹)"
              type="number"
              outlined
              dense
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" sm="6" />
          <v-col cols="12" sm="4">
            <v-text-field v-model="form.pfDeduction" label="PF Deduction (₹)" type="number" outlined dense />
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field v-model="form.tdsDeduction" label="TDS Deduction (₹)" type="number" outlined dense />
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field v-model="form.ptDeduction" label="PT Deduction (₹)" type="number" outlined dense />
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field v-model="form.pan" label="PAN (optional)" outlined dense />
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field v-model="form.uan" label="UAN (optional)" outlined dense />
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field v-model="form.esicNumber" label="ESIC No. (optional)" outlined dense />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field v-model="form.bankAccountNumber" label="Bank Account Number" outlined dense />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field v-model="form.bankIfsc" label="Bank IFSC" outlined dense />
          </v-col>
          <v-col cols="12">
            <v-textarea v-model="form.note" label="Note" outlined dense rows="2" />
          </v-col>
        </v-row>
      </v-form>
    </global-dialog>

    <!-- DEACTIVATE CONFIRM -->
    <global-dialog
      v-if="deactivateDialog"
      v-model="deactivateDialog"
      title="Deactivate Employee?"
      icon="mdi-account-off-outline"
      width="420"
      :buttons="deactivateDialogButtons"
    >
      <p>
        "{{ employeeToDeactivate?.name }}" will be excluded from future
        payroll runs. Already-run payroll history is unaffected.
      </p>
    </global-dialog>
  </v-row>
</template>

<script>
import AppAgGrid from "@/components/common/ag-grid.vue";
import GlobalDialog from "@/components/common/global-dialog.vue";

export default {
  name: "PayrollPage",
  components: { AppAgGrid, GlobalDialog },

  data() {
    return {
      activeTab: 0,
      employeeDialog: false,
      deactivateDialog: false,
      valid: false,
      saving: false,
      running: false,
      deactivating: false,

      editingEmployee: null,
      employeeToDeactivate: null,
      runPeriod: new Date().toISOString().slice(0, 7),
      runPeriodMenu: false,

      form: this.emptyForm(),

      rules: {
        required: (v) => (v !== null && v !== undefined && v !== "") || "Required",
      },

      employeeColumnDefs: [
        { headerName: "Name", field: "name", minWidth: 150 },
        { headerName: "Designation", field: "designation", valueFormatter: (p) => p.value || "-", minWidth: 140 },
        { headerName: "Gross Salary", field: "grossMonthlySalary", valueFormatter: (p) => this.formatINR(p.value), minWidth: 140 },
        {
          headerName: "Deductions",
          field: "deductions",
          valueGetter: (p) => p.data.pfDeduction + p.data.tdsDeduction + p.data.ptDeduction,
          valueFormatter: (p) => this.formatINR(p.value),
          minWidth: 130,
        },
        {
          headerName: "Net Pay",
          field: "netPay",
          valueGetter: (p) => p.data.grossMonthlySalary - p.data.pfDeduction - p.data.tdsDeduction - p.data.ptDeduction,
          valueFormatter: (p) => this.formatINR(p.value),
          minWidth: 130,
        },
        { headerName: "Status", field: "status", minWidth: 110 },
        {
          headerName: "Actions",
          field: "_id",
          minWidth: 100,
          sortable: false,
          filter: false,
          cellRenderer: (params) => {
            const deactivateIcon =
              params.data.status === "ACTIVE"
                ? `<i class="mdi mdi-account-off-outline pr-deactivate-employee" data-id="${params.data._id}" title="Deactivate Employee" style="cursor: pointer; font-size: 18px; color: #d93025; margin-left: 12px;"></i>`
                : "";
            return `
              <i class="mdi mdi-pencil-outline pr-edit-employee" data-id="${params.data._id}" title="Edit Employee" style="cursor: pointer; font-size: 18px; color: #475569;"></i>
              ${deactivateIcon}
            `;
          },
          onCellClicked: (params) => {
            if (params.event.target.classList.contains("pr-edit-employee")) {
              this.openEditDialog(params.data);
            } else if (params.event.target.classList.contains("pr-deactivate-employee")) {
              this.confirmDeactivate(params.data);
            }
          },
        },
      ],

      payrollRunColumnDefs: [
        { headerName: "Period", field: "period", minWidth: 110 },
        { headerName: "Employees", field: "employeeCount", valueGetter: (p) => p.data.lines.length, minWidth: 110 },
        { headerName: "Total Gross", field: "totalGross", valueFormatter: (p) => this.formatINR(p.value), minWidth: 130 },
        {
          headerName: "Total Deductions",
          field: "totalDeductions",
          valueGetter: (p) => p.data.totalPf + p.data.totalTds + p.data.totalPt,
          valueFormatter: (p) => this.formatINR(p.value),
          minWidth: 150,
        },
        { headerName: "Total Net Pay", field: "totalNetPay", valueFormatter: (p) => this.formatINR(p.value), minWidth: 140 },
        { headerName: "Run On", field: "createdAt", valueFormatter: (p) => this.formatDate(p.value), minWidth: 120 },
      ],
    };
  },

  computed: {
    employees() {
      return this.$store.state.employees.employees;
    },

    payrollRuns() {
      return this.$store.state.payroll.runs;
    },

    formattedRunPeriod() {
      if (!this.runPeriod) return "";
      const d = new Date(`${this.runPeriod}-01`);
      return d.toLocaleString("default", { month: "long", year: "numeric" });
    },

    employeeMobileCard() {
      const statusColors = {
        ACTIVE: { background: "#d1fae5", color: "#065f46" },
        INACTIVE: { background: "#e2e8f0", color: "#475569" },
      };

      return {
        title: (row) => row.name,
        subtitle: (row) => row.designation || "-",
        badge: (row) => ({
          text: row.status,
          style: statusColors[row.status] || { background: "#f1f5f9", color: "#475569" },
        }),
        fields: (row) => [
          { label: "Gross Salary", value: this.formatINR(row.grossMonthlySalary) },
          { label: "Net Pay", value: this.formatINR(row.grossMonthlySalary - row.pfDeduction - row.tdsDeduction - row.ptDeduction) },
        ],
        actions: (row) => {
          const actions = [
            { label: "Edit", icon: "mdi-pencil-outline", color: "#475569", onClick: () => this.openEditDialog(row) },
          ];
          if (row.status === "ACTIVE") {
            actions.push({ label: "Deactivate", icon: "mdi-account-off-outline", color: "#d93025", onClick: () => this.confirmDeactivate(row) });
          }
          return actions;
        },
      };
    },

    payrollRunMobileCard() {
      return {
        title: (row) => row.period,
        subtitle: (row) => `${row.lines.length} employee(s)`,
        fields: (row) => [
          { label: "Total Gross", value: this.formatINR(row.totalGross) },
          { label: "Total Net Pay", value: this.formatINR(row.totalNetPay) },
        ],
      };
    },

    employeeDialogButtons() {
      return [
        { text: "Cancel", action: () => (this.employeeDialog = false), outlined: true, color: "#0a5a90" },
        { text: "Save", action: this.submit, loading: this.saving },
      ];
    },

    deactivateDialogButtons() {
      return [
        { text: "Cancel", action: () => (this.deactivateDialog = false), outlined: true, color: "#0a5a90" },
        { text: "Deactivate", action: this.doDeactivate, color: "error", loading: this.deactivating },
      ];
    },
  },

  created() {
    this.fetchEmployees();
    this.fetchPayrollRuns();
  },

  methods: {
    fetchEmployees() {
      this.$store.dispatch("employees/getEmployees");
    },

    fetchPayrollRuns() {
      this.$store.dispatch("payroll/getPayrollRuns");
    },

    emptyForm() {
      return {
        name: "",
        designation: "",
        grossMonthlySalary: null,
        pfDeduction: 0,
        tdsDeduction: 0,
        ptDeduction: 0,
        pan: "",
        uan: "",
        esicNumber: "",
        bankAccountNumber: "",
        bankIfsc: "",
        note: "",
      };
    },

    formatINR(value) {
      if (value === null || value === undefined) return "0";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value);
    },

    formatDate(value) {
      if (!value) return "-";
      const d = new Date(value);
      return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    },

    openCreateDialog() {
      this.editingEmployee = null;
      this.form = this.emptyForm();
      this.employeeDialog = true;
    },

    openEditDialog(emp) {
      this.editingEmployee = emp;
      this.form = {
        name: emp.name,
        designation: emp.designation,
        grossMonthlySalary: emp.grossMonthlySalary,
        pfDeduction: emp.pfDeduction,
        tdsDeduction: emp.tdsDeduction,
        ptDeduction: emp.ptDeduction,
        pan: emp.pan,
        uan: emp.uan,
        esicNumber: emp.esicNumber,
        bankAccountNumber: emp.bankAccountNumber,
        bankIfsc: emp.bankIfsc,
        note: emp.note,
      };
      this.employeeDialog = true;
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        if (this.editingEmployee) {
          await this.$store.dispatch("employees/updateEmployee", {
            id: this.editingEmployee._id,
            employeeData: this.form,
          });
          this.$toast?.success("Employee updated successfully");
        } else {
          await this.$store.dispatch("employees/createEmployee", this.form);
          this.$toast?.success("Employee created successfully");
        }
        this.employeeDialog = false;
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to save employee");
      } finally {
        this.saving = false;
      }
    },

    confirmDeactivate(emp) {
      this.employeeToDeactivate = emp;
      this.deactivateDialog = true;
    },

    async doDeactivate() {
      this.deactivating = true;
      try {
        await this.$store.dispatch("employees/deactivateEmployee", this.employeeToDeactivate._id);
        this.$toast?.success("Employee deactivated successfully");
        this.deactivateDialog = false;
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to deactivate employee");
      } finally {
        this.deactivating = false;
      }
    },

    async doRunPayroll() {
      this.running = true;
      try {
        const result = await this.$store.dispatch("payroll/runPayroll", { period: this.runPeriod });
        this.$toast?.success(`Payroll run for ${result.payrollRun.period} completed — ${result.payrollRun.lines.length} employee(s), ${this.formatINR(result.payrollRun.totalNetPay)} net pay`);
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to run payroll");
      } finally {
        this.running = false;
      }
    },
  },
};
</script>

<style scoped>
.pr-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.pr-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.pr-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pr-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.pr-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.pr-disclaimer {
  margin-bottom: 20px;
  font-size: 13px;
}

.pr-tabs {
  margin-bottom: 20px;
}

.pr-tab-content {
  overflow: visible;
}

.pr-filter-bar {
  margin-bottom: 20px;
}

.pr-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.pr-add-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

</style>
